import { LazyExoticComponent, FC } from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { routePath, authPaths } from 'config/route-config';

import DirectSalesLanding from 'components/direct-sales-landing/DirectSalesLanding';
import EnergyNeeds from 'components/direct-sales/energy-needs/EnergyNeeds';
import PersonalDetails from 'components/direct-sales/personal-details/PersonalDetails';
import EvTariffInformation from 'components/direct-sales/ev-tariff-information-revamp/EvTariffInformation';
import DirectDebit from 'components/direct-sales/direct-debit-revamp/DirectDebit';
import MeterpointValidation from 'components/direct-sales/meterpoint-validation/MeterPointValidationRevamp';
import MeterpointGuide from 'components/direct-sales/meterpoint-guide/MeterpointGuideRevamp';
import SelectOptions from 'components/direct-sales/select-options/SelectOptionsRevamp';
import BusinessAnnouncement from 'components/direct-sales/business-updates-revamp/BusinessAnnouncement';
import SubmitEmail from 'components/direct-sales/business-updates-revamp/SubmitEmail';
import SubmitEmailSuccess from 'components/direct-sales/business-updates-revamp/SubmitEmailSuccess';
import Quote from 'components/direct-sales/quote/QuoteRevamp';
import PeakSave from 'components/direct-sales/peak-save/PeakSave';
import OrderSummary from 'components/direct-sales/order-summary-revamp/OrderSummary';
import OrderComplete from 'components/direct-sales/order-complete/OrderCompleteRevamp';
import Psr from 'components/direct-sales/psr-entry/PsrRevamp';
import EmailQuote from 'components/direct-sales/email-quote/EmailQuote';
import RetrieveQuote from 'components/retrieve-quote/RetrieveQuote';
import QuoteExpired from 'components/direct-sales/quote-expired/QuoteExpired';
import EmailCapture from 'components/direct-sales/email-capture/EmailCapture';
import AbandonedCart from 'components/abandoned-cart/AbandonedCart';
import QuoteExpiredAbandonedCart from 'components/abandoned-cart/components/QuoteExpired/QuoteExpired';
import SecondQuopte from 'components/second-quote/SecondQuote';

export type ScreenParts = {
  Template?: Component;
  Content?: Component;
  RightSidebar?: Component;
  LeftSidebar?: Component;
  Header?: Component;
  Skyline?: Component;
  Footer?: Component;
};

export type Component = (
  | FC<ScreenParts>
  | LazyExoticComponent<FC<ScreenParts>>
) & {
  preload?: () => Promise<unknown>;
};

export type Route = {
  path: string;
  name: string;
  step?: number;
  featureFlag?: string;
  isLanding?: boolean;
  isEnding?: boolean;
  noIndex?: boolean;
  component?: Component;
} & ScreenParts;

export const authRoutes = [
  {
    name: 'Auth Response',
    path: authPaths.authResponse,
    Content: lazy(() => import('components/app/routes/AuthResponse')),
    isLanding: true,
    noIndex: true
  }
];

export const partnerLandingRoutes: Route[] = [
  {
    name: 'Direct Sales',
    path: routePath.home,
    step: 1,
    isLanding: true,
    Content: DirectSalesLanding.Content,
    RightSidebar: DirectSalesLanding.RightSidebar,
    Header: DirectSalesLanding.Header,
    Footer: DirectSalesLanding.Footer
  },
  {
    name: 'Quote by Email',
    path: routePath.retrieveQuote,
    step: 1,
    Content: RetrieveQuote.Content,
    Header: RetrieveQuote.Header,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Abandoned Cart',
    path: routePath.abandonedCart,
    step: 1,
    isLanding: true,
    Content: AbandonedCart.Content,
    noIndex: true
  },
  {
    name: 'Second Quote',
    path: routePath.secondQuote,
    step: 1,
    isLanding: true,
    Content: SecondQuopte.Content,
    Header: SecondQuopte.Header,
    featureFlag: 'releaseSmoothSaleHandOff',
    noIndex: true
  }
];

export const journeyRoutes: Route[] = [
  {
    name: 'Business Announcement',
    path: routePath.businessUpdatesAnnouncement,
    Content: BusinessAnnouncement.Content,
    Header: BusinessAnnouncement.Header
  },
  {
    name: 'Email Submit',
    path: routePath.businessUpdatesSubmitEmail,
    Content: SubmitEmail.Content,
    Header: SubmitEmail.Header
  },
  {
    name: 'Email Submit Success',
    path: routePath.businessUpdatesSubmitEmailSuccess,
    Content: SubmitEmailSuccess.Content
  },
  {
    name: 'Select Options',
    path: routePath.selectOptions,
    Content: SelectOptions.Content,
    Header: SelectOptions.Header
  },
  {
    name: 'Meterpoint Validation',
    path: routePath.meterpointValidation,
    step: 1,
    Content: MeterpointValidation.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: MeterpointValidation.Header,
    Footer: MeterpointValidation.Footer
  },
  {
    name: 'Meterpoint Guide',
    path: routePath.meterpointGuide,
    step: 1,
    Content: MeterpointGuide.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: MeterpointGuide.Header,
    Footer: MeterpointValidation.Footer
  },
  {
    name: 'Your energy needs',
    path: routePath.energyNeeds,
    step: 1,
    Content: EnergyNeeds.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: EnergyNeeds.Header,
    Footer: EnergyNeeds.Footer
  },
  {
    name: 'EV tariff information',
    path: routePath.evTariffInformation,
    step: 1,
    Content: EvTariffInformation.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: EvTariffInformation.Header,
    Footer: EvTariffInformation.Footer
  },
  {
    name: 'Your tariff quote',
    path: routePath.quote,
    step: 2,
    Template: Quote.Template,
    Content: Quote.Content,
    LeftSidebar: Quote.LeftSidebar,
    Skyline: Quote.Skyline,
    Header: Quote.Header,
    Footer: Quote.Footer
  },
  {
    name: 'Peak Save',
    path: routePath.peakSave,
    Header: PeakSave.Header,
    Content: PeakSave.Content
  },
  {
    name: 'Email Quote',
    path: routePath.emailQuote,
    step: 3,
    Content: EmailQuote.Content
  },
  {
    name: 'Quote expired',
    path: routePath.quoteExpired,
    step: 1,
    Content: QuoteExpired.Content
  },
  {
    name: 'Account set up',
    path: routePath.personalDetails,
    step: 3,
    Content: PersonalDetails.Content,
    RightSidebar: PersonalDetails.RightSidebar,
    Header: PersonalDetails.Header
  },
  {
    name: 'Payment details',
    path: routePath.directDebit,
    step: 3,
    Content: DirectDebit.Content,
    RightSidebar: DirectDebit.RightSidebar,
    Header: DirectDebit.Header,
    Footer: DirectDebit.Footer
  },
  {
    name: 'Priority Services Register',
    path: routePath.psrEntry,
    step: 3,
    Content: Psr.Content,
    RightSidebar: Psr.RightSidebar,
    Header: Psr.Header
  },
  {
    name: 'Check your order',
    path: routePath.orderSummary,
    step: 3,
    Content: OrderSummary.Content,
    Header: OrderSummary.Header
  },
  {
    name: 'Confirmation',
    path: routePath.orderComplete,
    step: 3,
    isEnding: true,
    Content: OrderComplete.Content,
    Template: OrderComplete.Template
  },
  {
    name: 'Email Capture',
    path: routePath.emailCapture,
    Header: EmailCapture.Header,
    Content: EmailCapture.Content,
    RightSidebar: EmailCapture.RightSidebar,
    Footer: EmailCapture.Footer
  },
  {
    name: 'Quote Expired Abandoned Cart',
    path: routePath.quoteExpiredAbandonedCart,
    Content: QuoteExpiredAbandonedCart.Content,
    Footer: QuoteExpiredAbandonedCart.Footer
  }
];

export const routes = [
  ...journeyRoutes,
  ...partnerLandingRoutes,
  ...authRoutes
];
